const api = 'https://www.youtube.com/iframe_api';
let apiLoadedCallbacks = [];
let apiLoaded = false;

window.onYouTubeIframeAPIReady = () => {
  apiLoadedCallbacks.forEach(apiLoadedCallback => apiLoadedCallback());
  apiLoadedCallbacks = [];
  apiLoaded = true;
};

export default class YoutubeVideo {
  constructor(el) {
    this.el = el;
    this.onApiLoaded = this._onApiLoaded.bind(this);
    this.onStateChange = this._onStateChange.bind(this);
    this.onReady = this._onReady.bind(this);

    this.isReady = false;
    this.onReadyCallback = null;
    this.playerState = null;
    this.onPlayCallback = null;
    this.onPlayError = null;

    this.el.addEventListener('load', () => {
      if (apiLoaded) {
        this._onApiLoaded();
      } else {
        apiLoadedCallbacks.push(this.onApiLoaded);
        this._loadApi();
      }
    });
  }

  /**
   * Load the Youtube API: https://developers.google.com/youtube/iframe_api_reference
   * This code loads the IFrame Player API code asynchronously.
   * @private
   */
  _loadApi() {
    const tag = document.createElement('script');
    tag.src = api;

    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  }

  /**
   * Play the YouTube video. If player is not ready, add the callback so that it can
   * be played right after the player becomes ready.
   *
   * @returns {Promise} - whether the video was successfully started or not
   * @public
   */
  play() {
    return new Promise((resolve, reject) => {
      this.onPlayCallback = resolve;
      this.onPlayError = reject;

      if (this.isReady) {
        this.player.playVideo();
      } else {
        this.onReadyCallback = () => {
          this.player.playVideo();
        };
      }
    });
  }

  /**
   * Pause the video
   * @public
   */
  pause() {
    if (this.player) {
      this.player.pauseVideo();
    }
  }

  /**
   * Unload the player
   * @public
   */
  unload() {
    if (this.player) {
      this.player.destroy();
    }
  }

  /**
   * YouTube's API will call this function once the iFrame API has finished loading.
   * This method is pushed to the global scope so that the iFrame API can reach it.
   * @private
   */
  _onApiLoaded() {
    this.player = new YT.Player(
      this.el,
      {
        events: {
          onReady: this.onReady,
          onStateChange: this.onStateChange,
        },
      },
    );
  }

  /**
   * This function is called when the player is ready. We pass a reference to it
   * via the `events` parameter when setting up the player.
   * @private
   */
  _onReady() {
    this.isReady = true;

    if (this.onReadyCallback) {
      this.onReadyCallback();
    }
  }

  /**
   * This method is called whenever the state of the player changes. We pass a reference of this
   * to the player during initialization.
   *
   * @param {Event} event - event data that contains information about the state of the player
   * @private
   */
  _onStateChange(event) {
    const state = event.data;

    if (state === YT.PlayerState.PLAYING) {
      Core.Media.pauseAll(this.el);
    }

    if (this.onPlayCallback) {
      if (state === YT.PlayerState.PLAYING) {
        this.onPlayCallback();
        this.onPlayCallback = null;
        this.onPlayError = null;
      } else if (
        this.playerState === YT.PlayerState.BUFFERING
        && (state === YT.PlayerState.UNSTARTED
        || state === YT.PlayerState.PAUSED)
      ) {
        this.onPlayError();
        this.onPlayCallback = null;
        this.onPlayError = null;
      }
    }

    this.playerState = state;
  }
}
